import React from 'react';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';

/** Skeleton shapes per template (design screen 10). */
export function SkeletonBody({ kind = 'list' }) {
  if (kind === 'tile') {
    return (
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="rounded" width={40} height={40} />
        <Skeleton variant="text" width={90} height={36} />
        <Skeleton variant="text" width={140} height={18} />
      </Box>
    );
  }
  if (kind === 'chart') {
    return (
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-end', gap: 1.5, height: '100%' }}>
        {[60, 90, 45, 110, 80, 130, 70].map((height, index) => (
          <Skeleton key={index} variant="rounded" width={32} height={height} />
        ))}
      </Box>
    );
  }
  if (kind === 'timeline') {
    return (
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[0, 1, 2, 3, 4].map((index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="rounded" height={22} sx={{ flex: 1 }} />
          </Box>
        ))}
      </Box>
    );
  }
  if (kind === 'people') {
    return (
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index} variant="rounded" height={64} />
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ px: 2.25, py: 1 }}>
      {[0, 1, 2, 3, 4].map((index) => (
        <Skeleton key={index} variant="text" height={36} />
      ))}
    </Box>
  );
}

export function EmptyBody({ icon: Icon, text, action, testId }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        p: 2,
        textAlign: 'center',
        minHeight: 90,
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 28, color: '#94a3b8' }} /> : null}
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{text}</Typography>
      {action ? (
        <Typography
          component="button"
          type="button"
          onClick={action.onClick}
          sx={{
            background: 'none',
            border: 0,
            p: 0,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            color: 'secondary.main',
            fontFamily: 'inherit',
          }}
        >
          {action.label}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ErrorBody({ onRetry, testId }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid={testId}
      sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, minHeight: 90 }}
    >
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{t('dashboard.state.error')}</Typography>
      {onRetry ? (
        <IconButton size="small" onClick={onRetry} aria-label={t('dashboard.state.retry')} sx={{ color: 'secondary.main' }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

export function LockedOverlay({ onEnable }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(248,250,252,.82)',
        backdropFilter: 'grayscale(1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        borderRadius: '12px',
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: '#fff',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <LockIcon sx={{ fontSize: 20 }} />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('dashboard.state.locked')}</Typography>
      {onEnable ? (
        <Typography
          component="button"
          type="button"
          onClick={onEnable}
          sx={{ background: 'none', border: 0, p: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'secondary.main', fontFamily: 'inherit' }}
        >
          {t('dashboard.state.enableInSettings')}
        </Typography>
      ) : null}
    </Box>
  );
}
